import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Icon } from "@agentsaas/ui";
import { useT } from "../app/i18n";
import { useServices } from "../app/services";
import { nk } from "../data/notifications";
import { nativeBridge } from "../app/native";

/**
 * PWA 接线：
 * - registerServiceWorker：生产构建（或 VITE_SW=1）时注册 /sw.js；开发模式不注册，避免缓存干扰 HMR
 * - PwaBridge（放在路由内）：service worker 发来 navigate（点击系统通知）→ 站内跳转；push → 刷新通知列表；
 *   mock 模式下把 mock 后端的"推送"转给 service worker 展示（页面在后台时才会弹系统通知）
 * - OfflineBanner：离线提示（离线外壳由 service worker 提供）
 */

export function registerServiceWorker(env: { PROD: boolean; VITE_SW?: string } = import.meta.env) {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  // 原生壳：推送走 APNs / FCM，离线外壳由 App 自带，不注册 service worker
  if (nativeBridge()) return;
  if (!env.PROD && env.VITE_SW !== "1") return;
  const go = () => void navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => undefined);
  if (document.readyState === "complete") go();
  else window.addEventListener("load", go, { once: true });
}

export function PwaBridge() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { mock } = useServices();

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const sw = navigator.serviceWorker;
    const onMsg = (e: MessageEvent) => {
      const m = e.data as { type?: string; link?: string } | null;
      if (m?.type === "navigate" && typeof m.link === "string" && m.link.startsWith("/") && !m.link.startsWith("//")) navigate(m.link);
      else if (m?.type === "push") void qc.invalidateQueries({ queryKey: nk.list });
    };
    sw.addEventListener("message", onMsg);
    return () => sw.removeEventListener("message", onMsg);
  }, [navigate, qc]);

  useEffect(() => {
    if (!mock || typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    mock.notify.onPush = (m) => {
      const payload = { id: m.notificationId, category: m.category, code: m.code, title: m.title, body: m.body, link: m.link };
      navigator.serviceWorker.controller?.postMessage({ type: "show-notification", payload });
    };
    return () => {
      mock.notify.onPush = null;
    };
  }, [mock]);

  return null;
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine !== false));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

export function OfflineBanner() {
  const { t } = useT();
  const online = useOnline();
  if (online) return null;
  return (
    <div className="offline-banner" role="status" data-testid="offline-banner">
      <Icon name="wifiOff" size={16} />
      <span>{t("pwa.offline")}</span>
    </div>
  );
}
